import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AreaInput, EditorShell, Field, PanelShell, RowActions, TextInput, del, useCrud } from "@/components/admin/ui";

const empty = { title: "", date: "", venue: "", city: "", ticket_url: "", notes: "" };
const today = () => new Date().toISOString().slice(0, 10);

export const EventsPanel = () => {
  const { items, load } = useCrud("events");
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/events/${form.id}`, form);
      else await api.post("/events", form);
      toast.success("已保存");
      setForm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const sorted = [...items].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const upcomingCount = sorted.filter((e) => (e.date || "") >= today()).length;

  return (
    <PanelShell title={`演出日历（即将举行 ${upcomingCount}）`} onAdd={() => setForm({ ...empty })}>
      <div className="space-y-2" data-testid="admin-events-list">
        {sorted.length === 0 && (
          <div className="border border-dashed border-white/15 py-16 text-center">
            <p className="font-zh text-[#a1a1aa]">暂无演出档期 — 点击「新增」添加下一场演出</p>
          </div>
        )}
        {sorted.map((e) => {
          const past = (e.date || "") < today();
          return (
            <div key={e.id} className={`flex items-center justify-between gap-4 border border-white/10 bg-[#0a0a0a] px-4 py-3 ${past ? "opacity-50" : ""}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono2 text-xs text-[#ffde00]">{e.date || "—"}</span>
                  <span className={`font-mono2 text-[9px] px-1.5 py-0.5 ${past ? "bg-white/10 text-[#a1a1aa]" : "bg-[#ffde00] text-black"}`}>
                    {past ? "已结束" : "即将举行"}
                  </span>
                </div>
                <div className="font-zh text-sm text-white truncate mt-1">{e.title}</div>
                <div className="font-mono2 text-[10px] text-[#52525b]">
                  {[e.venue, e.city].filter(Boolean).join(" · ")}
                </div>
              </div>
              <RowActions
                onEdit={() => setForm(e)}
                onDelete={async () => {
                  if (await del("events", e.id, e.title)) load();
                }}
              />
            </div>
          );
        })}
      </div>
      {form && (
        <EditorShell title={form.id ? "编辑演出" : "新增演出"} onClose={() => setForm(null)} onSubmit={save} busy={busy}>
          <Field label="演出名称 TITLE">
            <TextInput required data-testid="admin-event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="DAESUNG 2025 ASIA TOUR : D's WAVE IN MALAYSIA" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="日期 DATE">
              <TextInput type="date" data-testid="admin-event-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label="城市 CITY">
              <TextInput value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="KUALA LUMPUR" />
            </Field>
          </div>
          <Field label="场馆 VENUE">
            <TextInput value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} placeholder="Axiata Arena" />
          </Field>
          <Field label="购票链接 TICKET URL">
            <TextInput value={form.ticket_url} onChange={(e) => setForm({ ...form, ticket_url: e.target.value })} placeholder="https://…" />
          </Field>
          <Field label="备注 NOTES">
            <AreaInput rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder=" Doors open 8PM" />
          </Field>
        </EditorShell>
      )}
    </PanelShell>
  );
};
